import { useEffect, useId, useState } from 'react'
import { api } from '../servizi/api.ts'
// `Campo` e' anche il nome del componente di base: il tipo dello schema si importa con un alias.
import type { Campo as CampoSchema, SchemaModulo } from '../servizi/api.ts'
import { AreaTesto, Campo, Scelta, Spunta } from './base.tsx'
import { Caselle } from './Pezzi.tsx'

/**
 * Campi del modulo di una tipologia di intervento.
 *
 * Li usano la registrazione degli interventi e le attivita' collettive: dopo D-26 le tipologie
 * con voci tra parentesi hanno un modulo, e cinque di loro si gestiscono come attivita'
 * collettive. Il modulo e' lo stesso e deve comparire allo stesso modo nei due punti.
 */

/**
 * Modulo pubblicato per una tipologia. Nullo senza tipologia, durante il caricamento e quando la
 * tipologia non ha modulo: in quel caso si registra senza campi aggiuntivi.
 */
export function usaModulo(tipologia: string): SchemaModulo | null {
  const [modulo, setModulo] = useState<SchemaModulo | null>(null)
  useEffect(() => {
    setModulo(null)
    if (!tipologia) return
    // Con due cambi di tipologia ravvicinati la risposta della prima non deve arrivare dopo
    // quella della seconda e sostituirne il modulo.
    let vivo = true
    api.get<{ schema_campi: SchemaModulo }>(`/riferimenti/modulo/${encodeURIComponent(tipologia)}`)
      .then((m) => { if (vivo) setModulo(m.schema_campi) })
      .catch(() => { if (vivo) setModulo(null) })
    return () => { vivo = false }
  }, [tipologia])
  return modulo
}

/** Etichetta di un campo: gli errori di validazione arrivano con il codice del campo. */
export function etichettaCampo(modulo: SchemaModulo | null, codice: string): string {
  return modulo?.campi.find((c) => c.codice === codice)?.etichetta ?? codice
}

/**
 * Un campo del modulo della tipologia, disegnato secondo il suo tipo. `errore` e' il messaggio della
 * validazione del modulo (MODULO_NON_VALIDO) per questo campo: si mostra sotto il campo e lo segna non
 * valido, cosi' il fuoco sul primo errore lo raggiunge.
 */
export function CampoModulo({ campo, valore, cambia, errore }: {
  campo: CampoSchema; valore: unknown; cambia: (v: unknown) => void; errore?: string | null
}) {
  const id = useId()
  if (campo.tipo === 'scelta_multipla') {
    const scelte = Array.isArray(valore) ? (valore as string[]) : []
    return (
      <fieldset className={`gruppo-caselle campo-doppia-colonna${errore ? ' campo-errato' : ''}`}
                aria-describedby={errore || campo.aiuto ? `${id}-nota` : undefined}>
        <legend className={campo.obbligatorio ? 'obbligatorio' : undefined}>{campo.etichetta}</legend>
        <Caselle voci={(campo.opzioni ?? []).map((o) => ({ codice: o.valore, nome: o.etichetta }))}
                 scelte={scelte} cambia={cambia}
                 riservate={campo.classe_dato === 'riservato' || campo.classe_dato === 'giudiziario'} />
        {errore
          ? <p className="campo-errore" id={`${id}-nota`} role="alert">{errore}</p>
          : campo.aiuto && <p className="campo-aiuto" id={`${id}-nota`}>{campo.aiuto}</p>}
      </fieldset>
    )
  }

  if (campo.tipo === 'testo_lungo') {
    return (
      <AreaTesto etichetta={campo.etichetta} obbligatorio={campo.obbligatorio} aiuto={campo.aiuto}
                 errore={errore} righe={3} valore={(valore as string) ?? ''} cambia={cambia} />
    )
  }

  if (campo.tipo === 'booleano') {
    // `Spunta` non espone `obbligatorio` ne' l'errore: l'asterisco si scrive nell'etichetta, con la
    // classe usata dai campi di base, e l'errore sotto, nell'involucro segnato come errato.
    return (
      <div className={errore ? 'campo-errato' : undefined}>
        <Spunta
          etichetta={campo.obbligatorio
            ? <>{campo.etichetta}<span className="campo-obbligatorio" aria-hidden="true"> *</span></>
            : campo.etichetta}
          aiuto={campo.aiuto} valore={!!valore} cambia={cambia}
        />
        {errore && <p className="campo-errore" role="alert">{errore}</p>}
      </div>
    )
  }

  if (campo.tipo === 'scelta' || campo.tipo === 'catalogo') {
    return (
      <Scelta etichetta={campo.etichetta} obbligatorio={campo.obbligatorio} aiuto={campo.aiuto}
              errore={errore} vuota="— seleziona —" opzioni={campo.opzioni ?? []}
              valore={(valore as string) ?? ''} cambia={(v) => cambia(v || null)} />
    )
  }

  return (
    <Campo etichetta={campo.etichetta} obbligatorio={campo.obbligatorio} aiuto={campo.aiuto} errore={errore}
           tipo={campo.tipo === 'numero' ? 'number' : campo.tipo === 'data' ? 'date' : 'text'}
           min={campo.min} max={campo.max}
           valore={(valore as string | number) ?? ''}
           cambia={(v) => cambia(campo.tipo === 'numero' ? (v === '' ? null : Number(v)) : v)} />
  )
}
